import React from 'react'
import { ShieldAlert } from 'lucide-react'
import { FloatingPanel } from './ui-primitives/floating-panel'
import { useSelector } from '../store'
import { CodeWidgetCheck } from './widgets/CodeWidget/analysis/use-code-widget-check'
import { CheckProgress, EmptyState, Summary } from './code-widget-diagnostics/app-verdict'
import { FocusRequest } from './code-widget-diagnostics/focus'

/**
 * Inspector Gadget: what code-widget-analysis.ts found in the app being configured, laid out for someone
 * who configures apps rather than reads pages. All judgement lives in the analyser, which is unit-tested;
 * the parts of this panel live in code-widget-diagnostics/.
 */
export function CodeWidgetDiagnosticsPopup(props: { check: CodeWidgetCheck; onClose: () => void }) {
  return (
    <FloatingPanel
      title="Inspector Gadget"
      icon={ShieldAlert}
      onClose={props.onClose}
      defaultSize={{ width: 1100, height: 720 }}
      minSize={{ width: 720, height: 400 }}
      maximizeStorageKey="inspector-gadget"
    >
      <CodeWidgetDiagnosticsContent check={props.check} />
    </FloatingPanel>
  )
}

/** The report without its window, so the new configurator UX can dock it as a side panel. */
export function CodeWidgetDiagnosticsContent(props: { check: CodeWidgetCheck }) {
  const config = useSelector((state) => state.currentViewer.config)
  const { analysis } = props.check
  const [focus, setFocus] = React.useState<FocusRequest | null>(null)

  return (
    /*
     * Tinted, so the cards read as raised rather than as sections of one page. A size container, so the
     * report can lay itself out for a docked side panel as well as for the wide floating window.
     */
    <div className="h-full overflow-auto bg-muted p-2 text-sm text-foreground [container-type:inline-size]">
      <CheckProgress check={props.check} />
      {analysis && (
        <>
          <Summary
            analysis={analysis}
            focus={focus}
            onShowFinding={(index, findingId, riskId) => setFocus({ index, findingId, riskId, nonce: Date.now() })}
          />
          {analysis.widgets.length === 0 && <EmptyState analysis={analysis} configLoaded={config != null} />}
        </>
      )}
    </div>
  )
}
